
import React, { useState, useEffect } from "react";
import "../css/youtube.css";
import { videos } from "../data/videos";
import logo from "../img/logos/escudo.png";

// Extraer el ID de videos de YouTube en sus formatos habituales.
const extractVideoId = (url) => {
    try {
        const parsedUrl = new URL(url);
        const hostname = parsedUrl.hostname.replace(/^www\./, "");

        if (hostname === "youtu.be" || hostname === "m.youtube.com") {
            return parsedUrl.pathname.split("/").filter(Boolean).pop() || "";
        }

        if (hostname === "youtube.com" || hostname === "music.youtube.com") {
            if (parsedUrl.searchParams.get("v")) {
                return parsedUrl.searchParams.get("v");
            }

            const parts = parsedUrl.pathname.split("/").filter(Boolean);
            if (["embed", "shorts", "live", "v"].includes(parts[0])) {
                return parts[1] || "";
            }
        }

        return "";
    } catch {
        return "";
    }
};

// Años disponibles ordenados del más reciente al más antiguo.
const years = [
    ...new Set(
        videos
            .map((video) => Number(video.year))
            .filter((year) => Number.isFinite(year))
    ),
].sort((a, b) => b - a);

const YouTubeVideoList = () => {
    // Se selecciona automáticamente el año más reciente.
    const [anioSeleccionado, setAnioSeleccionado] = useState(
        years[0] ?? new Date().getFullYear()
    );
    const [aniosDesplegados, setAniosDesplegados] = useState(false);
    const [videoSeleccionado, setVideoSeleccionado] = useState(null);

    const videosFiltrados = videos.filter(
        (video) => Number(video.year) === anioSeleccionado
    );

    const abrirVideo = (video) => {
        if (extractVideoId(video.url)) {
            setVideoSeleccionado(video);
        } else {
            console.error("No se pudo identificar el video de YouTube:", video.url);
        }
    };

    const cerrarVideo = () => setVideoSeleccionado(null);

    useEffect(() => {
        if (!videoSeleccionado) return undefined;

        const manejarTeclado = (e) => {
            if (e.key === "Escape") cerrarVideo();
        };

        window.addEventListener("keydown", manejarTeclado);
        return () => window.removeEventListener("keydown", manejarTeclado);
    }, [videoSeleccionado]);

    const videoId = videoSeleccionado
        ? extractVideoId(videoSeleccionado.url)
        : "";

    return (
        <section className="videos-container" id="videos-container">
            <h2 className="galeria-title">Galería Audiovisual</h2>

            <p className="galeria-subtitle">
                Revive nuestras ceremonias, actividades, proyectos y eventos más importantes.
                Un recorrido por la historia de nuestra comunidad educativa.
            </p>

            {/* Selector desplegable de años */}
            <div className="youtube-years">
                <h3 className="youtube-years-title">Explora nuestra historia</h3>
                <p className="youtube-years-subtitle">
                    Selecciona un año para descubrir nuestros recuerdos audiovisuales.
                </p>

                <div className="youtube-year-dropdown">
                    <button
                        type="button"
                        className={`youtube-year-selected ${aniosDesplegados ? "abierto" : ""}`}
                        onClick={() => setAniosDesplegados((anterior) => !anterior)}
                        aria-expanded={aniosDesplegados}
                        aria-label={`Año seleccionado ${anioSeleccionado}. ${aniosDesplegados ? "Ocultar años" : "Mostrar otros años"}`}
                    >
                        <span className="youtube-year-selected-icon">📅</span>
                        <span className="youtube-year-selected-text">Año {anioSeleccionado}</span>
                        <span className="youtube-year-arrow">▼</span>
                    </button>

                    {aniosDesplegados && (
                        <div className="youtube-year-options" role="group" aria-label="Años disponibles">
                            {years.map((year) => (
                                <button
                                    key={year}
                                    type="button"
                                    className={`youtube-year-option ${anioSeleccionado === year ? "activo" : ""}`}
                                    aria-pressed={anioSeleccionado === year}
                                    onClick={() => {
                                        setAnioSeleccionado(year);
                                        setAniosDesplegados(false);
                                    }}
                                >
                                    <span>{year}</span>
                                    {anioSeleccionado === year && (
                                        <span className="youtube-year-check">✓</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Videos del año seleccionado */}
            <div className="year-section">
                <div className="year-header">
                    <h2>📅 {anioSeleccionado}</h2>
                    <p>
                        {videosFiltrados.length}{" "}
                        {videosFiltrados.length === 1 ? "video disponible" : "videos disponibles"}
                    </p>
                </div>

                {videosFiltrados.length > 0 ? (
                    <div className="video-cards">
                        {videosFiltrados.map((video, index) => {
                            const id = extractVideoId(video.url);

                            return (
                                <article
                                    key={`${video.year}-${video.url}-${index}`}
                                    className="youtube-video-card"
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`Reproducir video: ${video.title}`}
                                    onClick={() => abrirVideo(video)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            abrirVideo(video);
                                        }
                                    }}
                                >
                                    <div className="thumbnail">
                                        {id ? (
                                            <img
                                                src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`}
                                                alt={`Miniatura: ${video.title}`}
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="youtube-thumbnail-fallback">Video de YouTube</div>
                                        )}
                                        <div className="play-icon">▶</div>
                                    </div>

                                    <div className="video-info">
                                        <h3>{video.title}</h3>
                                        {video.category && (
                                            <span className="youtube-category">{video.category}</span>
                                        )}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                ) : (
                    <div className="youtube-empty">
                        <div className="youtube-empty-icon">🎬</div>
                        <h3>Próximamente: videos de {anioSeleccionado}</h3>
                        <p>
                            Estamos preparando nuevos recuerdos audiovisuales para nuestra comunidad.
                        </p>
                    </div>
                )}
            </div>

            {/* Reproductor dentro de la misma página */}
            {videoSeleccionado && videoId && (
                <div className="youtube-modal" onClick={cerrarVideo} role="presentation">
                    <div
                        className="youtube-modal-content"
                        role="dialog"
                        aria-modal="true"
                        aria-label={`Reproductor: ${videoSeleccionado.title}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="youtube-modal-header">
                            <div className="youtube-modal-heading">
                                <span className="youtube-modal-label">GALERÍA AUDIOVISUAL</span>
                                <h3>{videoSeleccionado.title}</h3>
                            </div>
                            <button
                                type="button"
                                className="youtube-modal-close"
                                onClick={cerrarVideo}
                                aria-label="Cerrar reproductor"
                                autoFocus
                            >
                                ✕
                            </button>
                        </div>

                        <div className="youtube-player-wrapper">
                            <iframe
                                key={videoId}
                                src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1`}
                                title={videoSeleccionado.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                referrerPolicy="strict-origin-when-cross-origin"
                                allowFullScreen
                            />
                        </div>

                        <div className="youtube-modal-footer">
                            <span>Escuela Nueva Zelandia</span>
                            <button type="button" className="youtube-modal-close-text" onClick={cerrarVideo}>
                                Cerrar video
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Canal oficial */}
            <div className="channel-link">
                <img
                    src={logo}
                    alt="Escudo Escuela Nueva Zelandia"
                    className="channel-logo"
                    loading="lazy"
                />
                <h2 className="galeria-title">Canal Oficial de YouTube</h2>
                <p className="galeria-subtitle">
                    Más de {videos.length} videos registrados en nuestra galería para conocer las
                    actividades de nuestra comunidad educativa.
                </p>
                <a
                    href="https://www.youtube.com/channel/UCv9kvVQXl1zXASkSQfra55w"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="channel-link-btn"
                >
                    Visitar Canal Oficial
                </a>
            </div>
        </section>
    );
};

export default YouTubeVideoList;
